import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';

/**
 * Customer details request
 */
export interface CustomerDetailsRequest {
    idType: 'EID' | 'PASSPORT';
    idValue: string;
    nationality: string;
}

/**
 * Customer details response
 */
export interface CustomerDetailsResponse {
    status: 'success';
    data: {
        customer_id: string;
        registration_id: string;
        first_name: string;
        middle_name: string;
        last_name: string;
        phone: string;
        email: string;
        is_email_verified: boolean;
        is_phone_verified: boolean;
    };
}

/**
 * Customer details error response
 */
export interface CustomerDetailsErrorResponse {
    code: string;
    message: string;
    errors?: {
        field: string;
        message: string;
        code: string;
    }[];
}

/**
 * Verify details request
 */
export interface VerifyDetailsRequest {
    customer_id: string;
    country_code?: string; // Optional for email verification
    mobile?: string; // Optional for email verification
    email?: string; // Optional for phone verification
    registration_id: string;
    update_type?: 'Primary' | 'Secondary'; // Update type: Primary (default) or Secondary (for communication email from profile)
}

/**
 * Verify details response
 */
export interface VerifyDetailsResponse {
    status: 'success';
    details: {
        is_email_verified: boolean;
        is_phone_verified: boolean;
    };
}

/**
 * Verify details error response
 */
export interface VerifyDetailsErrorResponse {
    code: string;
    message: string;
    statusCode?: number;
}

/**
 * Registration status request
 */
export interface RegistrationStatusRequest {
    registration_id: string;
    id_type: 'EID' | 'PASSPORT';
    id_value: string;
    nationality: string;
    customer_id: string;
}

/**
 * Registration status response
 */
export interface RegistrationStatusResponse {
    status: 'success';
    data: {
        registration_status: string;
        message: string;
    };
}

/**
 * Registration status error response
 */
export interface RegistrationStatusErrorResponse {
    code: string;
    message: string;
    statusCode?: number;
}

/**
 * Customer details service
 */
class CustomerDetailsService {
    /**
     * Get customer details by EID/Passport and nationality
     */
    async getCustomerDetails(request: CustomerDetailsRequest): Promise<CustomerDetailsResponse> {
        try {
            logger.debug('CustomerDetailsService:Fetching customer details', request);

            // Build query string manually since apiClient.get expects URL with params
            const params = new URLSearchParams({
                idType: request.idType,
                idValue: request.idValue,
                nationality: request.nationality,
            });
            const url = `${Endpoints.registration.customerDetails}?${params.toString()}`;

            const response = await apiClient.get<CustomerDetailsResponse>(url);
            logger.debug('CustomerDetailsService:Customer details fetched', response);
            return response;
        } catch (error) {
            logger.error('CustomerDetailsService:Failed to fetch customer details', error);
            throw error;
        }
    }

    /**
     * Verify customer details (phone/email) after OTP verification
     */
    async verifyDetails(request: VerifyDetailsRequest): Promise<VerifyDetailsResponse> {
        try {
            logger.debug('CustomerDetailsService:Verifying customer details', request);

            const response = await apiClient.post<VerifyDetailsResponse>(
                Endpoints.registration.verifyDetails,
                request
            );
            logger.debug('CustomerDetailsService:Customer details verified', response);
            return response;
        } catch (error) {
            logger.error('CustomerDetailsService:Failed to verify customer details', error);
            throw error;
        }
    }

    /**
     * Get registration status
     */
    async getRegistrationStatus(request: RegistrationStatusRequest): Promise<RegistrationStatusResponse> {
        try {
            logger.debug('CustomerDetailsService:Fetching registration status', request);

            // Build query string manually since apiClient.get expects URL with params
            const params = new URLSearchParams({
                registration_id: request.registration_id,
                id_type: request.id_type,
                id_value: request.id_value,
                nationality: request.nationality,
                customer_id: request.customer_id,
            });
            const url = `${Endpoints.registration.registrationStatus}?${params.toString()}`;

            const response = await apiClient.get<RegistrationStatusResponse>(url);
            logger.debug('CustomerDetailsService:Registration status fetched', response);
            return response;
        } catch (error) {
            logger.error('CustomerDetailsService:Failed to fetch registration status', error);
            throw error;
        }
    }
}

export const customerDetailsService = new CustomerDetailsService();

